import React from 'react';
import './StatsHeader.css';
import Flex from '../../../../components/Flex/Flex';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectCustomDateFilterValue,
  selectDateFilterValue,
  selectGeneralStats,
  selectLimitFilterValue,
  selectPlayerToShowStats,
  selectTableSizeFilterValue,
} from '../../selectors';
import Text from '../../../../components/Text/Text';
import {
  changeDateFilter,
  changeLimitFilter,
  changeTableSizeFilter,
  dropFilters,
  findStats,
} from '../../actions';
import TableSizeFilter from '../../../../components/TableSizeFilter/TableSizeFilter';
import LimitFilter from '../../../../components/LimitFilter/LimitsFilter';
import DateFilter, { CustomDateFilterValue } from '../../../../components/DateFilter/DateFilter';
import { Tag } from 'antd';
import { getPlayerStatsHref, pokerSitePostfix } from '../../utils';
import FishBadge from '../../../../components/FishBadge/FishBadge';
import RegBadge from '../../../../components/RegBadge/RegBadge';
import PhoneBadge from '../../../../components/PhoneBadge/PhoneBadge';

const { CheckableTag } = Tag;

function shouldUseNativeLink(event: React.MouseEvent<HTMLAnchorElement>) {
  return event.button !== 0 || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey;
}

const StatsHeader = React.memo(() => {
  const dispatch = useDispatch();

  const playerToShowStats = useSelector(selectPlayerToShowStats);
  const generalStats = useSelector(selectGeneralStats);

  const tableSizeFilterValue = useSelector(selectTableSizeFilterValue);
  const limitFilterValue = useSelector(selectLimitFilterValue);
  const dateFilterValue = useSelector(selectDateFilterValue);
  const customDateFilterValue = useSelector(selectCustomDateFilterValue);

  const handleChangeTableSizeFilter = React.useCallback(
    (value?: number) => {
      dispatch(changeTableSizeFilter(value));
    },
    [dispatch],
  );

  const handleChangeLimitFilter = React.useCallback(
    (value: number) => {
      dispatch(changeLimitFilter(value));
    },
    [dispatch],
  );

  const handleChangeDateFilter = React.useCallback(
    (value?: number, customValue?: CustomDateFilterValue) => {
      dispatch(changeDateFilter({ value, customValue }));
    },
    [dispatch],
  );

  const handleSearchWithoutFilters = React.useCallback(() => {
    dispatch(dropFilters());
    dispatch(
      findStats({
        playername: playerToShowStats.playername,
        pokersiteId: playerToShowStats.pokersiteId,
      }),
    );
  }, [dispatch, playerToShowStats]);

  const handleSearchByPokersite = React.useCallback(
    (playername: string, pokersiteId: 1 | 2 | 3 | 4 | 5 | 6) => {
      dispatch(
        findStats({
          playername,
          pokersiteId,
        }),
      );
    },
    [dispatch],
  );

  const handleSameNickPlayerClick = React.useCallback(
    (
      event: React.MouseEvent<HTMLAnchorElement>,
      playername: string,
      pokersiteId: 1 | 2 | 3 | 4 | 5 | 6,
    ) => {
      if (shouldUseNativeLink(event)) {
        return;
      }

      event.preventDefault();
      handleSearchByPokersite(playername, pokersiteId);
    },
    [handleSearchByPokersite],
  );

  return (
    <div className="StatsHeader SurfaceCard SurfaceCard--compact">
      <Flex className="StatsHeader__Player" align="center" gap={12}>
        <Flex
          className="StatsHeader__Site"
          justify="center"
          align="center"
          onClick={handleSearchWithoutFilters}
        >
          <Text type="secondary" size={2}>
            {playerToShowStats.pokersitePostfix}
          </Text>
        </Flex>
        <Flex vertical gap={4} className="StatsHeader__PlayerInfo">
          <Flex align="center" gap={8} className="StatsHeader__PlayerNameRow">
            <Text bold size={5} className="StatsHeader__PlayerName">
              {playerToShowStats.playername}
            </Text>
            <FishBadge vpip={generalStats?.vpip} />
            <RegBadge
              hands={generalStats?.hands}
              bb100={generalStats?.bb100}
              vpip={generalStats?.vpip}
              pfr={generalStats?.pfr}
            />
            <PhoneBadge hands={generalStats?.hands} wtsd={generalStats?.sawshowdown} />
          </Flex>
          {playerToShowStats.sameNickPlayers.length > 0 && (
            <Flex gap={6} className="StatsHeader__SameNickPlayers" wrap>
              {playerToShowStats.sameNickPlayers.map((player) => (
                <a
                  key={player.id}
                  className="StatsHeader__SameNickPlayerLink"
                  href={getPlayerStatsHref(player.playername, player.pokersite_id)}
                  onClick={(event) =>
                    handleSameNickPlayerClick(event, player.playername, player.pokersite_id)
                  }
                >
                  <CheckableTag checked>{pokerSitePostfix[player.pokersite_id]}</CheckableTag>
                </a>
              ))}
            </Flex>
          )}
        </Flex>
      </Flex>

      <Flex gap={10} className="StatsHeader__Filters" align="flex-start">
        <TableSizeFilter value={tableSizeFilterValue} onChange={handleChangeTableSizeFilter} />
        <LimitFilter value={limitFilterValue} onChange={handleChangeLimitFilter} />
        <DateFilter
          value={dateFilterValue}
          customValue={customDateFilterValue}
          onChange={handleChangeDateFilter}
        />
      </Flex>
    </div>
  );
});

export default StatsHeader;
